import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import SlideHero from './SlideHero';
import MorphButton from './MorphButton';
import AppLogo from './AppLogo';

/**
 * A full-screen, one-question-per-screen form wizard — the playful
 * alternative to a long scrolling form. Each step gets its own hero icon,
 * title, subtitle and field, with a progress bar up top and a single
 * Continue button pinned to the bottom.
 *
 * Steps are plain closures over the caller's own React state, so no data
 * model / values object is imposed here:
 *   { id, hero, title, subtitle, render: () => node, isValid?: () => bool, footer?: () => node }
 */
export default function SlideFormShell({
  steps,
  onCancel,
  onFinish,
  // Runs before the finish animation. Return false (or a promise of false) to
  // cancel — this is where the sign-in gate hooks in.
  onBeforeFinish = () => true,
  finishLabel = 'Continue',
  finishMorphLabel,
  testId = 'slide-form',
  showBrand = true
}) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState('forward');

  const step = steps[index];
  const isLast = index === steps.length - 1;
  const valid = step.isValid ? step.isValid() : true;

  const goBack = () => {
    if (index === 0) {
      onCancel && onCancel();
      return;
    }
    setDirection('back');
    setIndex((i) => i - 1);
  };

  const goNext = async () => {
    if (!valid) return;
    if (isLast) {
      if ((await onBeforeFinish()) === false) return;
      onFinish && onFinish();
      return;
    }
    setDirection('forward');
    setIndex((i) => i + 1);
  };

  const enterClass =
    direction === 'forward'
      ? 'slide-in-from-right-8'
      : 'slide-in-from-left-8';

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-background" data-testid={testId}>
      {/* Compact brand row — keeps the IhsanOne logo visible even though these
          screens take over the whole viewport with no app header/nav. */}
      {showBrand && (
        <div className="flex flex-shrink-0 items-center justify-center gap-1.5 pt-4">
          <AppLogo size="sm" />
          <span className="text-sm font-extrabold leading-none tracking-tight">
            <span className="text-[#0F172A]">Ihsan</span>
            <span className="text-primary">One</span>
          </span>
        </div>
      )}

      {/* Back button + segmented progress bar */}
      <div className="flex flex-shrink-0 items-center gap-3 px-4 pb-2 pt-3 md:mx-auto md:w-full md:max-w-md">
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          data-testid={`${testId}-back`}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-muted"
        >
          <ArrowLeft className="h-5 w-5 text-[#0F172A]" />
        </button>
        <div className="flex flex-1 gap-1.5">
          {steps.map((s, i) => (
            <div
              key={s.id}
              className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                i <= index ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Slide content */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4 md:flex md:items-center md:justify-center">
        <div
          key={step.id}
          className={`animate-in fade-in-0 duration-300 ease-out md:mx-auto md:w-full md:max-w-md ${enterClass}`}
        >
          <SlideHero icon={step.hero} />
          <h1 className="text-center text-xl font-extrabold leading-snug text-[#0F172A]">{step.title}</h1>
          {step.subtitle && (
            <p className="mt-1.5 text-center text-sm leading-snug text-muted-foreground">{step.subtitle}</p>
          )}
          <div className="mt-6">{step.render()}</div>
        </div>
      </div>

      {/* Continue button */}
      <div className="flex-shrink-0 px-6 pb-6 pt-2 md:mx-auto md:w-full md:max-w-md">
        {isLast && finishMorphLabel ? (
          <MorphButton
            data-testid={`${testId}-continue`}
            idleLabel={finishLabel}
            successLabel={finishMorphLabel}
            disabled={!valid}
            onClick={onBeforeFinish}
            onComplete={onFinish}
            className="h-12 w-full rounded-xl bg-primary text-sm font-bold text-white hover:bg-primary-hover"
          />
        ) : (
          <button
            type="button"
            data-testid={`${testId}-continue`}
            onClick={goNext}
            disabled={!valid}
            className={`h-12 w-full rounded-xl text-sm font-bold transition-colors ${
              valid
                ? 'bg-primary text-white hover:bg-primary-hover'
                : 'cursor-not-allowed bg-muted text-muted-foreground'
            }`}
          >
            {isLast ? finishLabel : 'Continue'}
          </button>
        )}
        {step.footer && <div className="mt-3">{step.footer()}</div>}
      </div>
    </div>
  );
}
